import { createSelector } from 'reselect';

const selectPageMembershipsDomain = state => state.PageMemberships;

// Basic selectors
const makeSelectPageMemberships = () =>
  createSelector(selectPageMembershipsDomain, substate => substate);

const makeSelectPageMembershipsList = () =>
  createSelector(selectPageMembershipsDomain, substate => substate.pageMemberships);

const makeSelectPageMembershipsLoading = () =>
  createSelector(selectPageMembershipsDomain, substate => substate.loading);

const makeSelectPageMembershipsError = () =>
  createSelector(selectPageMembershipsDomain, substate => substate.error);

const makeSelectPageMembershipsLastUpdated = () =>
  createSelector(selectPageMembershipsDomain, substate => substate.lastUpdated);

const makeSelectPageMembershipsPagination = () =>
  createSelector(selectPageMembershipsDomain, substate => substate.pagination);

// Utility selectors
const makeSelectPageMembershipById = (membershipId) =>
  createSelector(
    selectPageMembershipsDomain,
    substate => substate.pageMemberships.find(membership => membership.id === membershipId)
  );

const makeSelectPageMembershipsByPage = (pageId) =>
  createSelector(
    selectPageMembershipsDomain,
    substate => substate.pageMemberships.filter(membership => membership.pageId === pageId)
  );

const makeSelectPageMembershipsByUser = (userId) =>
  createSelector(
    selectPageMembershipsDomain,
    substate => substate.pageMemberships.filter(membership => membership.userId === userId)
  );

const makeSelectPageMembershipsByRole = (roleId) =>
  createSelector(
    selectPageMembershipsDomain,
    substate => substate.pageMemberships.filter(membership => membership.roleId === roleId)
  );

const makeSelectPageMembershipsByStatus = (status) =>
  createSelector(
    selectPageMembershipsDomain,
    substate => substate.pageMemberships.filter(membership => membership.status === status)
  );

const makeSelectPageMembershipsActive = () =>
  createSelector(
    selectPageMembershipsDomain,
    substate => substate.pageMemberships.filter(membership => membership.status === 'active')
  );

const makeSelectPageMembershipsPending = () =>
  createSelector(
    selectPageMembershipsDomain,
    substate => substate.pageMemberships.filter(membership => membership.status === 'pending')
  );

const makeSelectPageMembershipsSearch = (searchTerm) =>
  createSelector(
    selectPageMembershipsDomain,
    substate => {
      if (!searchTerm) return substate.pageMemberships;
      const term = searchTerm.toLowerCase();
      return substate.pageMemberships.filter(membership =>
        (membership.userName && membership.userName.toLowerCase().includes(term)) ||
        (membership.userEmail && membership.userEmail.toLowerCase().includes(term)) ||
        (membership.roleName && membership.roleName.toLowerCase().includes(term)) ||
        (membership.pageName && membership.pageName.toLowerCase().includes(term))
      );
    }
  );

const makeSelectPageMembershipsGroupedByPage = () =>
  createSelector(
    selectPageMembershipsDomain,
    substate => {
      const grouped = {};
      substate.pageMemberships.forEach(membership => {
        const pageId = membership.pageId;
        if (!grouped[pageId]) {
          grouped[pageId] = [];
        }
        grouped[pageId].push(membership);
      });
      return grouped;
    }
  );

const makeSelectPageMembershipsGroupedByRole = () =>
  createSelector(
    selectPageMembershipsDomain,
    substate => {
      const grouped = {};
      substate.pageMemberships.forEach(membership => {
        const roleId = membership.roleId;
        if (!grouped[roleId]) {
          grouped[roleId] = [];
        }
        grouped[roleId].push(membership);
      });
      return grouped;
    }
  );

const makeSelectPageMembershipsGroupedByStatus = () =>
  createSelector(
    selectPageMembershipsDomain,
    substate => {
      const grouped = {};
      substate.pageMemberships.forEach(membership => {
        const status = membership.status;
        if (!grouped[status]) {
          grouped[status] = [];
        }
        grouped[status].push(membership);
      });
      return grouped;
    }
  );

const makeSelectPageMembershipsByUserAndPage = (userId, pageId) =>
  createSelector(
    selectPageMembershipsDomain,
    substate => substate.pageMemberships.find(membership =>
      membership.userId === userId && membership.pageId === pageId
    )
  );

const makeSelectPageMembershipsCountByPage = () =>
  createSelector(
    selectPageMembershipsDomain,
    substate => {
      const countByPage = {};
      substate.pageMemberships.forEach(membership => {
        const pageId = membership.pageId;
        countByPage[pageId] = (countByPage[pageId] || 0) + 1;
      });
      return countByPage;
    }
  );

const makeSelectPageMembershipsCountByRole = () =>
  createSelector(
    selectPageMembershipsDomain,
    substate => {
      const countByRole = {};
      substate.pageMemberships.forEach(membership => {
        const roleId = membership.roleId;
        countByRole[roleId] = (countByRole[roleId] || 0) + 1;
      });
      return countByRole;
    }
  );

export default makeSelectPageMemberships;
export {
  makeSelectPageMemberships,
  makeSelectPageMembershipsList,
  makeSelectPageMembershipsLoading,
  makeSelectPageMembershipsError,
  makeSelectPageMembershipsLastUpdated,
  makeSelectPageMembershipsPagination,
  makeSelectPageMembershipById,
  makeSelectPageMembershipsByPage,
  makeSelectPageMembershipsByUser,
  makeSelectPageMembershipsByRole,
  makeSelectPageMembershipsByStatus,
  makeSelectPageMembershipsActive,
  makeSelectPageMembershipsPending,
  makeSelectPageMembershipsSearch,
  makeSelectPageMembershipsGroupedByPage,
  makeSelectPageMembershipsGroupedByRole,
  makeSelectPageMembershipsGroupedByStatus,
  makeSelectPageMembershipsByUserAndPage,
  makeSelectPageMembershipsCountByPage,
  makeSelectPageMembershipsCountByRole,
};
